import React, {useCallback, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';


import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {
    SendWelcomeEmailTool,
    SubscribeToLcdCreditAnalysisTool,
    UnsubscribeFromLcdCreditAnalysisTool,
    UpdateUserAccountTool,
    UpdateUserNewsletterTool,
    UpdateUserStateTool,
} from 'src/modules/bulk-update';
import {AssignProductSeatTool} from 'src/modules/bulk-update/assign-product-seat';
import {UnassignProductSeatTool} from 'src/modules/bulk-update/unassign-product-seat';
import {
    DisableDeviceRegistrationSettingsTool,
    EnableDeviceRegistrationSettingsTool,
} from 'src/modules/bulk-update/update-device-registration';
import {
    DisableMfaSettingsTool,
    EnableMfaSettingsTool,
} from 'src/modules/bulk-update/update-mfa-settings';
import {
    AssignToGroupTool,
    UnassignFromGroupTool,
} from 'src/modules/bulk-update/update-user-group';
import {setUsers} from 'src/modules/bulk-update/update-users-entitlements/select-entitlements-step';
import {USER_KEYS_BY_TABS} from 'src/modules/profile/account/constants';
import {getAccountActiveUser} from 'src/openapi/admin/apis/GetAccountActiveUserApi';
import {useAppDispatch} from 'src/store/hooks';

import {useShown} from '@pb/kit-hooks-use-shown';

import {UsersGroupsToolsMenu} from './users-groups-tools-menu';
import {UsersGroupsToolsRibbonRow} from './users-groups-tools-ribbon-row';

import type {AccountActiveUserCount} from 'src/openapi/admin/models/AccountActiveUserCount';

type Props = {
    ids: number[],
    accountId: number,
    onDone: () => void,
};

export const UsersGroupsBulkActions = ({ids, accountId, onDone}: Props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();

    const {
        hasAccess,
        canBulkSetUserState,
        canBulkSetNewsletters,
        canBulkChangeUsersAccount,
        canBulkUpdateDeviceRegistration,
        canBulkUpdateMfa,
        canEditUserEntitlements,
        canBulkUpdateProducts,
    } = useAdminAccessRight();


    const {
        shown: assignToGroupShown,
        onClick: openAssignToGroup,
        onClose: closeAssignToGroup,
    } = useShown();
    const {
        shown: unassignProductSeatShown,
        onClick: openUnassignProductSeat,
        onClose: closeUnassignProductSeat,
    } = useShown();
    const {
        shown: assignProductSeatShown,
        onClick: openAssignProductSeat,
        onClose: closeAssignProductSeat,
    } = useShown();
    const {
        shown: dischargeToGroupShown,
        onClick: openDischargeToGroup,
        onClose: closeDischargeToGroup,
    } = useShown();
    const {
        shown: enableDeviceRegistrationShown,
        onClick: openEnableDeviceRegistration,
        onClose: closeEnableDeviceRegistration,
    } = useShown();
    const {
        shown: disableDeviceRegistrationShown,
        onClick: openDisableDeviceRegistration,
        onClose: closeDisableDeviceRegistration,
    } = useShown();
    const {
        shown: enableMfaSettingsShown,
        onClick: openEnableMfaSettings,
        onClose: closeEnableMfaSettings,
    } = useShown();
    const {
        shown: disableMfaSettingsShown,
        onClick: openDisableMfaSettings,
        onClose: closeDisableMfaSettings,
    } = useShown();
    const {
        shown: updateUserStateToolShown,
        onClick: openUpdateUserStateTool,
        onClose: closeUpdateUserStateTool,
    } = useShown();
    const {
        shown: updateUserNewsletterToolShown,
        onClick: openUpdateUserNewsletterTool,
        onClose: closeUpdateUserNewsletterTool,
    } = useShown();
    const {
        shown: updateUserAccountToolShown,
        onClick: openUpdateUserAccountTool,
        onClose: closeUpdateUserAccountTool,
    } = useShown();
    const {
        shown: sendWelcomeEmailToolShown,
        onClick: openSendWelcomeEmailTool,
        onClose: closeSendWelcomeEmailTool,
    } = useShown();
    const {
        shown: subscribeToLcdAnalysisToolShown,
        onClick: openSubscribeToLcdAnalysisTool,
        onClose: closeSubscribeToLcdAnalysisTool,
    } = useShown();
    const {
        shown: unsubscribeFromLcdAnalysisToolShown,
        onClick: openUnsubscribeFromLcdAnalysisTool,
        onClose: closeUnsubscribeFromLcdAnalysisTool,
    } = useShown();

    const showUpdateGroup = useMemo(() => (
        hasAccess(ACCESS_RIGHTS.BULK_ACCOUNT_GROUP_UPDATE)
    ), [hasAccess]);

    const showUpdateDeviceRegistration = useMemo(() => (
        canBulkUpdateDeviceRegistration(accountId)
    ), [accountId, canBulkUpdateDeviceRegistration]);

    const showUpdateMfaSettings = useMemo(() => (
        canBulkUpdateMfa(accountId)
    ), [accountId, canBulkUpdateMfa]);

    const showUpdateUserState = useMemo(() => (
        canBulkSetUserState(accountId)
    ), [accountId, canBulkSetUserState]);

    const showUpdateUserNewsletter = useMemo(() => (
        canBulkSetNewsletters(accountId)
    ), [canBulkSetNewsletters, accountId]);

    const showUpdateUserAccount = useMemo(() => (
        canBulkChangeUsersAccount(accountId)
    ), [canBulkChangeUsersAccount, accountId]);

    const showUpdateLcdAnalysis = useMemo(() => (
        hasAccess(ACCESS_RIGHTS.LCD_CREDIT_ANALYSIS_BULK_UPDATE)
    ), [hasAccess]);

    const showUpdateUserEntitlements = useMemo(() => (
        canEditUserEntitlements(accountId)
    ), [accountId, canEditUserEntitlements]);

    const showAssignProductSeat = useMemo(() =>
        canBulkUpdateProducts(accountId), [accountId, canBulkUpdateProducts]);
    const showUnassignProductSeat = useMemo(() =>
        canBulkUpdateProducts(accountId), [accountId, canBulkUpdateProducts]);

    const toolsCount = useMemo(() => [
        showUpdateGroup,
        showUpdateDeviceRegistration,
        showUpdateMfaSettings,
        showUpdateUserState,
        showUpdateUserNewsletter,
        showUpdateUserAccount,
        showUpdateLcdAnalysis,
        true,
        showUpdateUserEntitlements,
        showAssignProductSeat,
        showUnassignProductSeat,
    ].filter(Boolean).length, [
        showUpdateGroup,
        showUpdateDeviceRegistration,
        showUpdateMfaSettings,
        showUpdateUserState,
        showUpdateUserNewsletter,
        showUpdateUserAccount,
        showUpdateLcdAnalysis,
        showUpdateUserEntitlements,
        showAssignProductSeat,
        showUnassignProductSeat,
    ]);

    const openUsersEntitlementsAssignTool = useCallback(() => {
        dispatch(setUsers(ids));
        navigate(`/profile/account/${accountId}/assign-users-entitlements`);
    }, [accountId, navigate, ids, dispatch]);

    const openUsersEntitlementsDischargeTool = useCallback(() => {
        dispatch(setUsers(ids));
        navigate(`/profile/account/${accountId}/discharge-users-entitlements`);
    }, [accountId, navigate, ids, dispatch]);

    const refreshPage = useCallback(async () => {
        const usersResponse = await apiDispatch(getAccountActiveUser({accountId}));

        const pathMatch = window.location.pathname.match(/\/profile\/account\/(\d+)\/users-groups\/(\w+)/);
        if (!pathMatch) {
            return;
        }

        const [, accountIdFromPath, stateType] = pathMatch;
        if (parseInt(accountIdFromPath, 10) !== accountId || !stateType) {
            return;
        }

        const usersState = USER_KEYS_BY_TABS[stateType] as keyof AccountActiveUserCount;
        if (!usersState && !['all', 'upload'].includes(stateType)) {
            return;
        }

        onDone();

        if (usersResponse.payload.success) {
            const users = usersResponse.payload.data;
            const currentCount = users[usersState];
            if (!currentCount && stateType !== 'all') {
                navigate(`/profile/account/${accountId}/users-groups/all`);
            }
        }
    }, [apiDispatch, accountId, onDone, navigate]);

    return (
        <>
            {toolsCount < 6 ? (
                <UsersGroupsToolsRibbonRow
                    showUpdateGroup={showUpdateGroup}
                    openAssignToGroup={openAssignToGroup}
                    openDischargeFromGroup={openDischargeToGroup}

                    showUpdateDeviceRegistration={showUpdateDeviceRegistration}
                    openEnableDeviceRegistration={openEnableDeviceRegistration}
                    openDisableDeviceRegistration={openDisableDeviceRegistration}

                    showUpdateMfaSettings={showUpdateMfaSettings}
                    openEnableMfaSettings={openEnableMfaSettings}
                    openDisableMfaSettings={openDisableMfaSettings}

                    showUpdateUserState={showUpdateUserState}
                    openUpdateUserStateTool={openUpdateUserStateTool}

                    showUpdateUserNewsletter={showUpdateUserNewsletter}
                    openUpdateUserNewsletterTool={openUpdateUserNewsletterTool}

                    showUpdateUserAccount={showUpdateUserAccount}
                    openUpdateUserAccountTool={openUpdateUserAccountTool}

                    showSendWelcomeEmail={true}
                    openSendWelcomeEmailTool={openSendWelcomeEmailTool}

                    showUpdateLcdAnalysis={showUpdateLcdAnalysis}
                    openSubscribeToLcdAnalysisTool={openSubscribeToLcdAnalysisTool}
                    openUnsubscribeFromLcdAnalysisTool={openUnsubscribeFromLcdAnalysisTool}

                    showUpdateUserEntitlements={showUpdateUserEntitlements}
                    openUsersEntitlementsAssignTool={openUsersEntitlementsAssignTool}
                    openUsersEntitlementsDischargeTool={openUsersEntitlementsDischargeTool}
                    showAssignProductSeat={showAssignProductSeat}
                    openAssignProductSeat={openAssignProductSeat}
                    showUnassignProductSeat={showUnassignProductSeat}
                    openUnassignProductSeat={openUnassignProductSeat}
                />
            ) : (
                <UsersGroupsToolsMenu
                    showUpdateGroup={showUpdateGroup}
                    openAssignToGroup={openAssignToGroup}
                    openDischargeFromGroup={openDischargeToGroup}

                    showUpdateDeviceRegistration={showUpdateDeviceRegistration}
                    openEnableDeviceRegistration={openEnableDeviceRegistration}
                    openDisableDeviceRegistration={openDisableDeviceRegistration}

                    showUpdateMfaSettings={showUpdateMfaSettings}
                    openEnableMfaSettings={openEnableMfaSettings}
                    openDisableMfaSettings={openDisableMfaSettings}

                    showUpdateUserState={showUpdateUserState}
                    openUpdateUserStateTool={openUpdateUserStateTool}

                    showUpdateUserNewsletter={showUpdateUserNewsletter}
                    openUpdateUserNewsletterTool={openUpdateUserNewsletterTool}

                    showUpdateUserAccount={showUpdateUserAccount}
                    openUpdateUserAccountTool={openUpdateUserAccountTool}

                    showSendWelcomeEmail={true}
                    openSendWelcomeEmailTool={openSendWelcomeEmailTool}

                    showUpdateLcdAnalysis={showUpdateLcdAnalysis}
                    openSubscribeToLcdAnalysisTool={openSubscribeToLcdAnalysisTool}
                    openUnsubscribeFromLcdAnalysisTool={openUnsubscribeFromLcdAnalysisTool}

                    showUpdateUserEntitlements={showUpdateUserEntitlements}
                    openUsersEntitlementsAssignTool={openUsersEntitlementsAssignTool}
                    openUsersEntitlementsDischargeTool={openUsersEntitlementsDischargeTool}
                    showAssignProductSeat={showAssignProductSeat}
                    openAssignProductSeat={openAssignProductSeat}
                    showUnassignProductSeat={showUnassignProductSeat}
                    openUnassignProductSeat={openUnassignProductSeat}
                />
            )}
            <AssignToGroupTool
                shown={assignToGroupShown}
                userIds={ids as Array<number>}
                accountIdsSelector={() => [accountId]}
                onDone={refreshPage}
                onClose={closeAssignToGroup}
            />
            <UnassignFromGroupTool
                shown={dischargeToGroupShown}
                userIds={ids as Array<number>}
                onDone={refreshPage}
                onClose={closeDischargeToGroup}
            />
            <AssignProductSeatTool
                shown={assignProductSeatShown}
                accountId={accountId}
                userIds={ids}
                onDone={refreshPage}
                onClose={closeAssignProductSeat}
            />
            <UnassignProductSeatTool
                shown={unassignProductSeatShown}
                accountId={accountId}
                userIds={ids}
                onDone={refreshPage}
                onClose={closeUnassignProductSeat}
            />
            <EnableDeviceRegistrationSettingsTool
                shown={enableDeviceRegistrationShown}
                userIds={ids as Array<number>}
                onDone={refreshPage}
                onClose={closeEnableDeviceRegistration}
            />
            <DisableDeviceRegistrationSettingsTool
                shown={disableDeviceRegistrationShown}
                userIds={ids as Array<number>}
                onDone={refreshPage}
                onClose={closeDisableDeviceRegistration}
            />
            <EnableMfaSettingsTool
                shown={enableMfaSettingsShown}
                usersIds={ids as Array<number>}
                onDone={refreshPage}
                onClose={closeEnableMfaSettings}
            />
            <DisableMfaSettingsTool
                shown={disableMfaSettingsShown}
                usersIds={ids as Array<number>}
                onDone={refreshPage}
                onClose={closeDisableMfaSettings}
            />
            <UpdateUserStateTool
                shown={updateUserStateToolShown}
                userIds={ids as Array<number>}
                onDone={refreshPage}
                onClose={closeUpdateUserStateTool}
            />
            <UpdateUserNewsletterTool
                shown={updateUserNewsletterToolShown}
                userIds={ids as Array<number>}
                onDone={refreshPage}
                onClose={closeUpdateUserNewsletterTool}
            />
            <UpdateUserAccountTool
                shown={updateUserAccountToolShown}
                userIds={ids as Array<number>}
                onDone={refreshPage}
                onClose={closeUpdateUserAccountTool}
            />
            <SendWelcomeEmailTool
                shown={sendWelcomeEmailToolShown}
                userIds={ids as Array<number>}
                accountId={accountId}
                onDone={refreshPage}
                onClose={closeSendWelcomeEmailTool}
            />
            <SubscribeToLcdCreditAnalysisTool
                shown={subscribeToLcdAnalysisToolShown}
                usersIds={ids}
                onDone={refreshPage}
                onClose={closeSubscribeToLcdAnalysisTool}
            />
            <UnsubscribeFromLcdCreditAnalysisTool
                shown={unsubscribeFromLcdAnalysisToolShown}
                usersIds={ids}
                onDone={refreshPage}
                onClose={closeUnsubscribeFromLcdAnalysisTool}
            />
        </>
    );
};
